import {useEffect, useMemo, useState} from "react";
import {Box, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import {toggleGroupSx} from "../../intel/panels/resolvers/editors/ResolverUtils.tsx";
import type {ContentWordCount} from "../../../../sdo/ContentWordCount.ts";
import type {ContentFramePointer} from "../../../../sdo/ContentFramePointer.ts";
import type {WordGram} from "../../../../sdo/WordGram.ts";
import ContentWordCard from "./content/ContentWordCard.tsx";
import ContentWordDetail from "./content/ContentWordDetail.tsx";
import ContentWordGrams from "./content/ContentWordGrams.tsx";
import ResizableSplit from "../../../common/ResizableSplit.tsx";

interface WordData {
    words: ContentWordCount[];
    frames: ContentFramePointer[];
    grams: Record<string, { left: WordGram[]; right: WordGram[]; inFrame: WordGram[] }>;
}

// TODO: Replace with API calls to AResourceProfileManager.readContentWordSummary() + readContentSeries()
function buildTestData(): WordData {
    return {
        words: [
            {word: "investigation", count: 42, acronym: 0, allLower: 38, allUpper: 0, camelCase: 0, mixedCase: 0, titleCase: 4},
            {word: "subject", count: 37, acronym: 0, allLower: 31, allUpper: 2, camelCase: 0, mixedCase: 0, titleCase: 4},
            {word: "network", count: 29, acronym: 0, allLower: 25, allUpper: 1, camelCase: 0, mixedCase: 0, titleCase: 3},
            {word: "DNS", count: 24, acronym: 24, allLower: 0, allUpper: 0, camelCase: 0, mixedCase: 0, titleCase: 0},
            {word: "correlation", count: 19, acronym: 0, allLower: 18, allUpper: 0, camelCase: 0, mixedCase: 0, titleCase: 1},
            {word: "threat", count: 16, acronym: 0, allLower: 12, allUpper: 1, camelCase: 0, mixedCase: 0, titleCase: 3},
            {word: "segment", count: 14, acronym: 0, allLower: 13, allUpper: 0, camelCase: 0, mixedCase: 0, titleCase: 1},
            {word: "timeline", count: 11, acronym: 0, allLower: 9, allUpper: 0, camelCase: 0, mixedCase: 0, titleCase: 2},
        ],
        frames: [
            {
                uid: "f1", frameNumber: 1,
                page: 1, section: 1, paragraph: 1, sentence: 1,
                originalText: "The investigation into the subject revealed network anomalies consistent with DNS exfiltration patterns.",
                pointers: [{uid: "p1", typeKey: "user_profile", display: "Analyst Alpha"}],
            },
            {
                uid: "f2", frameNumber: 2,
                page: 1, section: 1, paragraph: 2, sentence: 1,
                originalText: "Correlation of threat indicators across the timeline confirmed the subject was active during the period.",
                pointers: [],
            },
            {
                uid: "f3", frameNumber: 3,
                page: 3, section: 2, paragraph: 1, sentence: 3,
                originalText: "DNS log correlation on segment 192.168.4.0/24 provided additional evidence for the ongoing investigation.",
                pointers: [
                    {uid: "p2", typeKey: "network_address", display: "192.168.4.0/24"},
                ],
            },
            {
                uid: "f4", frameNumber: 4,
                page: 5, section: 1, paragraph: 1, sentence: 1,
                originalText: "The threat assessment timeline indicates escalation following the network segment compromise.",
                pointers: [],
            },
        ],
        grams: {
            investigation: {
                left: [{word: "the", count: 12}, {word: "ongoing", count: 8}, {word: "an", count: 5}, {word: "this", count: 3}],
                right: [{word: "into", count: 9}, {word: "revealed", count: 6}, {word: "confirmed", count: 4}],
                inFrame: [{word: "subject", count: 8}, {word: "network", count: 6}, {word: "DNS", count: 5}, {word: "correlation", count: 4}],
            },
            subject: {
                left: [{word: "the", count: 15}, {word: "named", count: 4}, {word: "primary", count: 3}],
                right: [{word: "was", count: 10}, {word: "revealed", count: 5}, {word: "identified", count: 3}],
                inFrame: [{word: "investigation", count: 8}, {word: "network", count: 5}, {word: "timeline", count: 3}],
            },
            network: {
                left: [{word: "the", count: 10}, {word: "target", count: 4}, {word: "internal", count: 3}],
                right: [{word: "segment", count: 7}, {word: "anomalies", count: 5}, {word: "traffic", count: 3}],
                inFrame: [{word: "DNS", count: 7}, {word: "segment", count: 6}, {word: "threat", count: 4}],
            },
            DNS: {
                left: [{word: "via", count: 6}, {word: "suspicious", count: 3}],
                right: [{word: "log", count: 8}, {word: "exfiltration", count: 4}, {word: "lookups", count: 3}],
                inFrame: [{word: "correlation", count: 6}, {word: "network", count: 5}, {word: "segment", count: 5}],
            },
            correlation: {
                left: [{word: "DNS", count: 6}, {word: "log", count: 5}, {word: "cross", count: 3}],
                right: [{word: "of", count: 7}, {word: "confirmed", count: 4}, {word: "on", count: 3}],
                inFrame: [{word: "DNS", count: 6}, {word: "investigation", count: 4}, {word: "segment", count: 3}],
            },
            threat: {
                left: [{word: "the", count: 8}, {word: "active", count: 3}],
                right: [{word: "indicators", count: 5}, {word: "assessment", count: 4}, {word: "landscape", count: 3}],
                inFrame: [{word: "timeline", count: 5}, {word: "network", count: 4}, {word: "segment", count: 3}],
            },
            segment: {
                left: [{word: "network", count: 7}, {word: "target", count: 3}],
                right: [{word: "192.168", count: 5}, {word: "compromise", count: 3}],
                inFrame: [{word: "DNS", count: 5}, {word: "network", count: 6}, {word: "correlation", count: 3}],
            },
            timeline: {
                left: [{word: "the", count: 6}, {word: "activity", count: 3}],
                right: [{word: "of", count: 4}, {word: "indicates", count: 3}, {word: "confirmed", count: 2}],
                inFrame: [{word: "threat", count: 5}, {word: "subject", count: 3}, {word: "investigation", count: 2}],
            },
        },
    };
}

function UXResourceWordsPanel() {
    const [data] = useState<WordData>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [selectedWord, setSelectedWord] = useState<string | null>(null);

    const filtered = useMemo(() => {
        let result = data.words;
        if (filterText.trim()) {
            const q = filterText.toLowerCase();
            result = result.filter((w) => w.word.toLowerCase().includes(q));
        }
        result = [...result].sort((a, b) => {
            if (sortBy === "name") {
                const cmp = a.word.localeCompare(b.word);
                return sortDir === "asc" ? cmp : -cmp;
            } else {
                const cmp = a.count - b.count;
                return sortDir === "asc" ? cmp : -cmp;
            }
        });
        return result;
    }, [data, filterText, sortBy, sortDir]);

    // Auto-select first word
    useEffect(() => {
        if (filtered.length === 0) {
            if (selectedWord !== null) setSelectedWord(null);
            return;
        }
        const stillVisible = filtered.some((w) => w.word === selectedWord);
        if (!stillVisible) {
            setSelectedWord(filtered[0].word);
        }
    }, [filtered, selectedWord]);

    const selected = useMemo(() => {
        if (!selectedWord) return null;
        return filtered.find((w) => w.word === selectedWord) || null;
    }, [filtered, selectedWord]);

    // Frames containing the selected word
    const selectedFrames = useMemo(() => {
        if (!selectedWord) return [];
        const q = selectedWord.toLowerCase();
        return data.frames.filter((f) => f.originalText.toLowerCase().includes(q));
    }, [data, selectedWord]);

    // Grams for the selected word
    const selectedGrams = useMemo(() => {
        if (!selectedWord || !data.grams[selectedWord]) return {left: [], right: [], inFrame: []};
        return data.grams[selectedWord];
    }, [data, selectedWord]);

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Content Words</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center">
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter words..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>
                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {data.words.length}
                </Typography>
            </Stack>

            {/* 3-way split: word list (left) + detail (center) + grams (right) */}
            <Stack direction="row" spacing={0} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {/* Left: word card list */}
                <Box sx={{
                    flex: selected ? "0 0 20%" : "1 1 100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    paddingRight: selected ? "12px" : 0,
                    borderRight: selected ? "1px solid #3F464F" : "none",
                    overflowY: "auto",
                    backgroundColor: "rgba(0, 71, 106, 0.15)",
                    borderRadius: "8px",
                    padding: "8px",
                }}>
                    <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                        <Typography sx={{
                            color: "#a8b8c0",
                            fontSize: "0.95rem",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textAlign: "left",
                        }}>
                            Select a Word
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{marginTop: "10px"}}>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={sortBy}
                                onChange={(_e, v) => { if (v) setSortBy(v); }}
                                sx={{...toggleGroupSx, "& .MuiToggleButton-root": {...(toggleGroupSx as Record<string, unknown>)["& .MuiToggleButton-root"] as Record<string, unknown>, fontSize: "0.65rem", padding: "2px 6px", minWidth: 0}}}
                            >
                                <ToggleButton value="name">Name</ToggleButton>
                                <ToggleButton value="count">Count</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={sortDir}
                                onChange={(_e, v) => { if (v) setSortDir(v); }}
                                sx={{...toggleGroupSx, "& .MuiToggleButton-root": {...(toggleGroupSx as Record<string, unknown>)["& .MuiToggleButton-root"] as Record<string, unknown>, fontSize: "0.65rem", padding: "2px 6px", minWidth: 0}}}
                            >
                                <ToggleButton value="asc">Asc</ToggleButton>
                                <ToggleButton value="desc">Desc</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "flex-start"}}>
                        {filtered.map((w) => (
                            <ContentWordCard
                                key={w.word}
                                word={w}
                                selected={selectedWord === w.word}
                                onClick={(word) => setSelectedWord(word.word)}
                            />
                        ))}
                    </Box>
                    {filtered.length === 0 && (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.4)",
                            fontSize: "0.80rem",
                            textAlign: "center",
                            padding: "24px",
                        }}>
                            No words match the current filter
                        </Typography>
                    )}
                </Box>

                {/* Right side of the panel: resizable horizontal split between center (case counts + frames) and right (n-grams) */}
                {selected && (
                    <Box sx={{
                        flex: "1 1 80%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "row",
                        paddingLeft: "12px",
                    }}>
                        <ResizableSplit
                            direction="horizontal"
                            defaultRatio={0.5}
                            minRatio={0.2}
                            maxRatio={0.8}
                            first={(
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                    paddingRight: "12px",
                                }}>
                                    <Box sx={{
                                        background: "linear-gradient(to right, rgba(41, 53, 64, 0.95), rgba(13, 14, 15, 0.95))",
                                        border: "1px solid #38ff82",
                                        borderRadius: "8px",
                                        padding: "8px 14px",
                                        marginBottom: "12px",
                                    }}>
                                        <Typography sx={{
                                            color: "#02cfee",
                                            fontSize: "1.30rem",
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                        }}>
                                            {selected.word}
                                        </Typography>
                                    </Box>
                                    <ContentWordDetail word={selected} frames={selectedFrames}/>
                                </Box>
                            )}
                            second={(
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                    paddingLeft: "12px",
                                }}>
                                    <ContentWordGrams
                                        leftWords={selectedGrams.left}
                                        rightWords={selectedGrams.right}
                                        inFrameWords={selectedGrams.inFrame}
                                    />
                                </Box>
                            )}
                        />
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceWordsPanel;
